import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

// Scope toggle mirrors the other phases — Active default, All for full visibility.
const SCOPE_OPTS = [
  { v: "active", l: "Active" },
  { v: "all",    l: "All" },
];

// Gradient palette by ascending bucket index — low buckets cool, high warm.
// Capped at 7 colors to match both PRICE and KM 7-bucket layouts.
const PRICE_COLORS = [
  "#6FA8DC", "#22C55E", "#84CC16", "#EAB308", "#F5A623", "#FF8C42", "#FF1E1E",
];

function fmtPct(n) {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(1)}%`;
}

function ClusterTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-[#0E0E0E] border border-[#2A2A2A] px-3 py-2 text-xs font-mono-data">
      <div className="text-white font-display normal-case mb-1">{label}</div>
      <div className="text-white/70">Count: <span className="text-white">{row.count}</span></div>
      <div className="text-white/70">Share: <span className="text-white">{fmtPct(row.pct)}</span></div>
    </div>
  );
}

// Single distribution panel — bar chart + breakdown table.
// Reused for Asking / Offer / Retail / KM.
function ClusterCard({ title, subtitle, panel, testid }) {
  const rows = panel?.rows || [];
  const scored = panel?.scored || 0;
  const unknown = panel?.unknown_count || 0;
  return (
    <div className="qd-card p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3 gap-3">
        <div className="min-w-0">
          <div className="qd-overline truncate">{title}</div>
          {subtitle && <div className="text-[11px] text-white/45 mt-1 truncate">{subtitle}</div>}
        </div>
        <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/40 shrink-0">
          {scored} scored
          {unknown > 0 && <span className="text-white/25"> · {unknown} missing</span>}
        </div>
      </div>

      {/* Chart */}
      <div className="h-[180px] sm:h-[200px] w-full min-w-0">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <BarChart data={rows} margin={{ top: 8, right: 4, left: -22, bottom: 4 }}>
            <XAxis dataKey="bucket" tick={{ fill: "#888", fontSize: 9 }} axisLine={false} tickLine={false} interval={0} />
            <YAxis tick={{ fill: "#666", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip cursor={{ fill: "#FF1E1E10" }} content={<ClusterTooltip />} />
            <Bar dataKey="count" radius={[2, 2, 0, 0]}>
              {rows.map((r, i) => (
                <Cell key={r.bucket} fill={PRICE_COLORS[i % PRICE_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Breakdown table */}
      <table className="w-full text-xs font-mono-data mt-3" data-testid={`${testid}-table`}>
        <thead>
          <tr className="text-[10px] tracking-widest uppercase text-white/40 border-b border-[#1A1A1A]">
            <th className="px-2 py-1.5 text-left">Bucket</th>
            <th className="px-2 py-1.5 text-right">Count</th>
            <th className="px-2 py-1.5 text-right">Share</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={r.bucket}
              className="border-b border-[#141414]"
              data-testid={`${testid}-row-${r.bucket.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
            >
              <td className="px-2 py-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="inline-block w-2.5 h-2.5 shrink-0" style={{ background: PRICE_COLORS[i % PRICE_COLORS.length] }} />
                  <span className="text-white truncate">{r.bucket}</span>
                </div>
              </td>
              <td className="px-2 py-1.5 text-right text-white">{r.count}</td>
              <td className="px-2 py-1.5 text-right text-white/80">{fmtPct(r.pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PricingClusters() {
  const [scope, setScope] = useState("active");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/analytics/pricing-clusters?scope=${scope}`)
      .then((r) => !cancelled && setData(r.data))
      .catch(() => toast.error("Failed to load pricing clusters"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [scope]);

  return (
    <div data-testid="pricing-clusters">
      {/* Scope toggle — header-right slot via CollapsibleSection prop */}
      <div className="flex items-center justify-end mb-3 -mt-2">
        <div className="inline-flex border border-[#2A2A2A] bg-[#0E0E0E]" data-testid="pc-scope-tabs">
          {SCOPE_OPTS.map((s) => (
            <button
              key={s.v}
              data-testid={`pc-scope-${s.v}`}
              onClick={() => setScope(s.v)}
              className={`px-3 py-1.5 text-[10px] font-mono-data tracking-widest uppercase border-r border-[#2A2A2A] last:border-r-0 transition-colors ${
                scope === s.v ? "bg-[#FF1E1E]/15 text-[#FF1E1E]" : "text-white/55 hover:text-white"
              }`}
            >
              {s.l}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="qd-card p-10 text-center text-xs text-white/40 font-mono-data tracking-wide">
          Loading pricing clusters…
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
          <ClusterCard
            title="Asking Price Clusters"
            subtitle="Seller's initial ask"
            panel={data?.asking_price}
            testid="pc-asking"
          />
          <ClusterCard
            title="Offer Price Clusters"
            subtitle="Our current admin offer"
            panel={data?.offer_price}
            testid="pc-offer"
          />
          <ClusterCard
            title="Retail Value Clusters"
            subtitle="Estimated retail value"
            panel={data?.retail_value}
            testid="pc-retail"
          />
          <ClusterCard
            title="KM Clusters"
            subtitle="Kilometers driven"
            panel={data?.km}
            testid="pc-km"
          />
        </div>
      )}
    </div>
  );
}
